import { useEffect, useState } from 'react';
import { Bell, X } from 'lucide-react';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import { useAppContext } from '@/contexts/AppContext';
import { toast } from 'sonner';

const STORAGE_KEY = 'sola-push-onboarding';

interface State {
  dismissedAt?: number;
  permanentlyDismissed?: boolean;
}

const getState = (): State => {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}'); } catch { return {}; }
};
const setState = (s: State) => localStorage.setItem(STORAGE_KEY, JSON.stringify(s));

const PushOnboardingBanner = () => {
  const { user } = useAppContext();
  const { isSupported, isSubscribed, subscribe, loading } = usePushNotifications();
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!user || loading || !isSupported || isSubscribed) { setShow(false); return; }
    if (typeof Notification !== 'undefined' && Notification.permission === 'denied') { setShow(false); return; }

    const s = getState();
    if (s.permanentlyDismissed) { setShow(false); return; }
    // Re-prompt after 7 days
    if (s.dismissedAt && Date.now() - s.dismissedAt < 7 * 24 * 60 * 60 * 1000) { setShow(false); return; }
    // Slight delay so it doesn't pop instantly on load
    const t = setTimeout(() => setShow(true), 2500);
    return () => clearTimeout(t);
  }, [user, loading, isSupported, isSubscribed]);

  if (!show) return null;

  const handleEnable = async () => {
    const ok = await subscribe();
    if (ok) {
      toast.success('Notificações ativadas!', { description: 'Você receberá lembretes devocionais e novidades.' });
      setState({});
    } else {
      toast.error('Não foi possível ativar', { description: 'Verifique as permissões do navegador.' });
    }
    setShow(false);
  };

  const handleLater = () => {
    setState({ dismissedAt: Date.now() });
    setShow(false);
  };

  const handleNever = () => {
    setState({ permanentlyDismissed: true });
    setShow(false);
  };

  return (
    <div className="fixed bottom-20 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-2rem)] max-w-md animate-in slide-in-from-bottom-4 fade-in duration-300">
      <div className="rounded-2xl border border-border bg-card shadow-xl p-4">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-accent/10 text-accent">
            <Bell size={18} />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="font-serif text-base text-foreground leading-tight">Receba lembretes devocionais</h3>
            <p className="text-xs text-subtle mt-1 leading-snug">
              Mantenha sua jornada espiritual diária com lembretes suaves no horário que você preferir.
            </p>
          </div>
          <button onClick={handleLater} className="text-subtle hover:text-foreground p-1 -mt-1 -mr-1" aria-label="Fechar">
            <X size={16} />
          </button>
        </div>
        <div className="flex gap-2 mt-3">
          <button onClick={handleEnable} className="flex-1 py-2 rounded-lg bg-primary text-primary-foreground text-xs font-medium hover:opacity-90 transition-opacity">
            Ativar notificações
          </button>
          <button onClick={handleLater} className="px-3 py-2 rounded-lg border border-border text-xs font-medium text-subtle hover:text-foreground transition-colors">
            Depois
          </button>
          <button onClick={handleNever} className="text-[10px] text-subtle/60 hover:text-subtle px-1" title="Não perguntar novamente">
            Nunca
          </button>
        </div>
      </div>
    </div>
  );
};

export default PushOnboardingBanner;
